import { getCodeFromErrors } from '@common/components';
import type { ApolloError } from '@common/hooks';

// Maps a regenerateScheduleRun failure to user-facing copy. The backend raises each failure
// as a top-level GraphQL execution error carrying `extensions.code`. Unknown / network errors
// and the UNPROCESSABLE_ENTITY catch-all fall back to generic copy — the raw backend message
// can include internal IDs, so it is never surfaced.
export function regenerateErrorMessage(error: ApolloError): string {
  switch (getCodeFromErrors(error.graphQLErrors)) {
    case 'NOT_FOUND':
      return 'We could not find this service line or scheduling period. Refresh and try again.';
    case 'RUN_IN_PROGRESS':
      return 'A schedule is already being generated for this period. Please wait for it to finish.';
    case 'RUN_ALREADY_ACTIVE':
      return 'A schedule was just generated for this period. Refresh and try again.';
    default:
      return 'We could not generate the schedule. Please try again.';
  }
}

// Title for the "generate schedules" confirmation modal. Falls back while the service line
// name is still loading so the modal never reads "...for undefined?".
export function generateSchedulesModalTitle(
  serviceLineName?: string | null
): string {
  return serviceLineName
    ? `Generate schedules for ${serviceLineName}?`
    : 'Generate schedules?';
}

// Summary line for the confirmation modal: names the service line and, once resolved, the
// period range. Both have load-time fallbacks.
export function generateSchedulesModalSummary(
  serviceLineName?: string | null,
  periodLabel?: string | null
): string {
  const scope = serviceLineName ?? 'this service line';
  const period = periodLabel ? ` for ${periodLabel}` : '';
  return `This will run the balancer across all units in ${scope}${period}.`;
}
